import { PassportItem, PassportSection } from '@/config'
import { SDS_CANONICAL_PREFIX } from './useItemNamesFromLocalesFile'
import { FieldValueType, MappingData as MappingDataItem } from '@coac-gmbh/saifty-orchestrator-api'

export enum Mappings {
  Saifty = 'SAIFTY',
  DataSpace = 'DataSpace',
  SAP = 'SAP',
}

interface MappingSystemDefinition {
  key: string
  getName: (row: MappingDataItem) => string
  getMappingTo: (row: MappingDataItem) => string
}

export interface MappingData {
  mappings: MappingDataItem[]
  targetPassportId: string
}

export interface ExtendedMappingData extends MappingDataItem, MappingPair {
  fieldName: string
  index?: number
}

export interface MappingPair {
  saiftyCanonical: string
  dataSpacePath: string
}

export interface SectionItemConfig {
  mode: 'selectable' | 'free'
  allowToEditSectionName: boolean
  modelValueKey: keyof PassportItem
  modelTitleKey?: keyof PassportItem
}

const MappingSystems: Record<string, MappingSystemDefinition> = {
  Saifty: {
    key: Mappings.Saifty,
    getName: (row) => row.fieldName,
    getMappingTo: (row) => row.inputPath,
  },

  DataSpace: {
    key: Mappings.DataSpace,
    getName: (row) => row.inputPath.replace(/^\$\./, ''),
    getMappingTo: (row) => row.fieldName,
  },
}

export const MAPPING_ARRAY_SUFFIX = '[*]'
export const CERTIFICATE_MAPPING_ID = 'static_mapping-company-certificate'

export function getInitialEmptySection(): PassportSection {
  return {
    name: '',
    items: [],
    sections: [],
  }
}

export function getInitialEmptyItem() {
  return {
    name: '',
  }
}

export function applySaiftyParsing(item: PassportItem): void {
  const match = item.name.match(/^(\d+(?:_\d+)*)\.(.+)$/)

  if (!match) return

  const [, prefix, field] = match

  item.saiftyPrefix = prefix
  item.name = field
}

export default function useMappings() {
  type UnknownObject = Record<string, unknown>

  function isAnObjectWithoutObjectsInside(value: unknown): value is UnknownObject {
    return typeof value === 'object' && value !== null && !Array.isArray(value)
  }

  function passportToJSON(sections: PassportSection[], modelValueKey: keyof PassportItem): UnknownObject {
    return sections.reduce<UnknownObject>((acc, section) => {
      const sectionName = cleanArraySymbolFromName(section.name)
      if (!sectionName) {
        acc = { ...acc, ...mapMappingSectionToObject(section, modelValueKey) }
      } else {
        acc[sectionName] = mapMappingSectionToObject(section, modelValueKey)
      }
      return acc
    }, {})
  }

  function JSONToPassport(data: UnknownObject, sectionName = 'root'): PassportSection {
    const items: { name: string }[] = []
    const sections: PassportSection[] = []

    for (const [key, value] of Object.entries(data)) {
      // Array case
      if (Array.isArray(value)) {
        const first = value[0]
        const arrayName = `${key}${MAPPING_ARRAY_SUFFIX}`

        if (isAnObjectWithoutObjectsInside(first)) {
          const hasNestedObjects = Object.values(first).some(
            (v) => isAnObjectWithoutObjectsInside(v) || Array.isArray(v)
          )

          if (hasNestedObjects) {
            sections.push(JSONToPassport(first, arrayName))
          } else {
            sections.push({
              name: arrayName,
              items: Object.keys(first).map((k) => ({ name: k })),
              sections: [],
            })
          }
        } else {
          items.push({ name: arrayName })
        }

        continue
      }

      // Object and primitive case
      if (isAnObjectWithoutObjectsInside(value)) {
        const hasNestedObjects = Object.values(value).some((v) => isAnObjectWithoutObjectsInside(v) || Array.isArray(v))

        if (hasNestedObjects) {
          sections.push(JSONToPassport(value, key))
        } else {
          sections.push({
            name: key,
            items: Object.keys(value).map((k) => ({ name: k })),
            sections: [],
          })
        }
      } else {
        items.push({ name: key })
      }
    }

    return {
      name: sectionName,
      items,
      sections,
    }
  }

  function mapMappingSectionToObject(
    section: PassportSection,
    modelValueKey: keyof PassportItem
  ): UnknownObject | UnknownObject[] {
    function getValue(item: PassportItem) {
      return (item[modelValueKey] as string) ?? ''
    }

    // Array case
    if (Array.isArray(section.instances) || Array.isArray(section.instancesSections)) {
      const maxLength = Math.max(section.instances?.length || 0, section.instancesSections?.length || 0)

      if (maxLength === 0 && section.items?.length) {
        const obj: UnknownObject = {}
        section.items.forEach((item) => {
          if (!item?.name) return
          obj[cleanArraySymbolFromName(item.name)] = getValue(item)
        })
        return [obj]
      }

      return Array.from({ length: maxLength }).map((_, index) => {
        const obj: UnknownObject = {}

        const instanceItems = section.instances?.[index] || []
        const instanceSections = section.instancesSections?.[index] || []

        if (instanceItems.length > 0) {
          instanceItems.forEach((item) => {
            if (!item?.name) return
            obj[cleanArraySymbolFromName(item.name)] = getValue(item)
          })
        } else if (instanceSections.length > 0) {
          instanceSections.forEach((subSection) => {
            subSection.items.forEach((item) => {
              if (!item?.name) return
              obj[cleanArraySymbolFromName(item.name)] = getValue(item)
            })
          })
        }

        section.sections.forEach((childSection, childIndex) => {
          const key = cleanArraySymbolFromName(childSection.name)
          const sectionToUse = instanceSections[childIndex] || childSection
          let value
          if (
            !sectionToUse.instances?.length &&
            !sectionToUse.instancesSections?.length &&
            sectionToUse.items?.length
          ) {
            const tempObj: UnknownObject = {}
            sectionToUse.items.forEach((item) => {
              if (!item?.name) return
              tempObj[cleanArraySymbolFromName(item.name)] = getValue(item)
            })
            value = [tempObj]
          } else {
            value = mapMappingSectionToObject(sectionToUse, modelValueKey)
          }

          obj[cleanArraySymbolFromName(key)] = cleanArraySymbolFromName(childSection.name)
            ? Array.isArray(value)
              ? value
              : [value]
            : value
        })

        return obj
      })
    }

    // Flatted case
    const result: UnknownObject = {}
    section.items.forEach((item) => {
      if (!item?.name) return
      result[cleanArraySymbolFromName(item.name)] = getValue(item)
    })

    section.sections.forEach((childSection) => {
      const key = cleanArraySymbolFromName(childSection.name)
      let value = mapMappingSectionToObject(childSection, modelValueKey)

      if (childSection.name.includes(MAPPING_ARRAY_SUFFIX)) {
        value = Array.isArray(value) ? value : [value]
        result[cleanArraySymbolFromName(key)] = value
      } else {
        result[key] = value
      }
    })

    return result
  }

  function cleanArraySymbolFromName(name = '') {
    return name.replace(MAPPING_ARRAY_SUFFIX, '')
  }

  function saiftyToPassport(saifty: string[]): PassportSection[] {
    const roots = new Map<string, PassportSection>()

    for (const entry of saifty) {
      const [left, itemName] = entry.split('.')
      if (!left || !itemName) continue

      const parts = left.split('_')
      if (parts[0] !== 'SEC' || parts.length < 3) continue

      const l1 = parts[1] // 15
      const l2 = parts[2] // 1
      const extra = parts.slice(3) // ["2","1"]

      const rootName = `${SDS_CANONICAL_PREFIX}${l1}`

      // Level 0
      if (!roots.has(rootName)) {
        roots.set(rootName, {
          name: rootName,
          items: [],
          sections: [],
        })
      }

      const root = roots.get(rootName)!

      // Level 1
      let subsection = root.sections.find((s) => s.name === l2)
      if (!subsection) {
        subsection = {
          name: l2,
          items: [],
          sections: [],
        }
        root.sections.push(subsection)
      }

      // Item
      if (extra.length) {
        const prefix = extra.join('_')
        subsection.items.push({
          name: `${prefix}.${itemName}`,
          saiftyPrefix: prefix,
        })
      } else {
        subsection.items.push({
          name: itemName,
        })
      }
    }

    return Array.from(roots.values())
  }

  function passportToSaifty(sections: PassportSection[]): string[] {
    const result: string[] = []

    for (const root of sections) {
      const rootMatch = root.name.match(/^SEC_(\d+)$/)
      if (!rootMatch) continue

      const l1 = rootMatch[1]

      for (const subsection of root.sections) {
        const l2 = subsection.name

        for (const item of subsection.items) {
          // The saiftyPrefix is calculated in the component
          if (item.saiftyPrefix) {
            result.push(`${SDS_CANONICAL_PREFIX}${l1}_${l2}_${item.saiftyPrefix}.${item.name.split('.').pop()}`)
          } else {
            result.push(`${SDS_CANONICAL_PREFIX}${l1}_${l2}.${item.name}`)
          }
        }
      }
    }

    return result
  }

  function extractMappingsFromDataSpace(source: PassportSection[], modelValueKey: keyof PassportItem = 'mappingTo') {
    const mappings: ExtendedMappingData[] = []

    function getItemValue(item: PassportItem): string | undefined {
      return item[modelValueKey] as string
    }

    function traverse(section: PassportSection, path: string[], inheritedIndex?: number) {
      const currentPath = [...path, section.name].filter(Boolean)

      // Array case
      if (section.instances && section.instances.length > 0) {
        section.instances.forEach((instance, instanceIndex) => {
          const index = instanceIndex + 1

          instance.forEach((item) => {
            const value = getItemValue(item)
            if (!value) return

            const inputPath = buildInputPath(currentPath, item.name)
            const dataSpacePath = buildDataSpacePath(currentPath, item.name)

            mappings.push({
              fieldName: value,
              inputPath,
              index,
              fieldValueType: FieldValueType.Value,
              saiftyCanonical: value,
              dataSpacePath,
            })
          })

          // Important: traverse the sections with the index of the instance
          section.sections.forEach((child) => {
            traverse(child, currentPath, index)
          })
        })

        return
      }

      // Flattened case
      section.items.forEach((item) => {
        const value = getItemValue(item)
        if (!value) return

        const inputPath = buildInputPath(currentPath, item.name)
        const dataSpacePath = buildDataSpacePath(currentPath, item.name)

        mappings.push({
          fieldName: value,
          inputPath,
          index: inheritedIndex,
          fieldValueType: FieldValueType.Value,
          saiftyCanonical: value,
          dataSpacePath,
        })
      })

      section.sections.forEach((child) => {
        traverse(child, currentPath, inheritedIndex)
      })
    }

    source.forEach((root) => {
      traverse(root, [])
    })

    return mappings
  }

  function buildDataSpacePath(sectionPath: string[], itemName: string): string {
    return [...sectionPath, itemName].filter(Boolean).join('.')
  }

  function buildInputPath(sectionPath: string[], itemName: string): string {
    return `$.${buildDataSpacePath(sectionPath, itemName)}`
  }

  function getOrCreateSection(sections: PassportSection[], name: string): PassportSection {
    let section = sections.find((s) => s.name === name)
    if (!section) {
      section = { name, items: [], sections: [] }
      sections.push(section)
    }
    return section
  }

  function dataSpaceToSaifty(dataSpace: PassportSection[]): PassportSection[] {
    const mappings = extractMappingsFromDataSpace(dataSpace)

    const saiftyTree = saiftyToPassport(mappings.map((m) => m.saiftyCanonical))

    // Insert mappingTo
    for (const { saiftyCanonical, dataSpacePath } of mappings) {
      attachMappingToSaiftyItem(saiftyTree, saiftyCanonical, dataSpacePath)
    }

    return saiftyTree
  }

  function attachMappingToSaiftyItem(tree: PassportSection[], saiftyCanonical: string, mappingTo: string) {
    const [left, itemName] = saiftyCanonical.split('.')
    const parts = left.split('_')

    const rootName = `${SDS_CANONICAL_PREFIX}${parts[1]}`
    const l2 = parts[2]
    const extra = parts.slice(3).join('_')

    const root = tree.find((s) => s.name === rootName)
    if (!root) return

    const sub = root.sections.find((s) => s.name === l2)
    if (!sub) return

    const expectedName = extra ? `${extra}.${itemName}` : itemName

    const item = sub.items.find((i) => i.name === expectedName)
    if (!item) return

    item.mappingTo = mappingTo
  }

  function saiftyToDataSpace(source: PassportSection[]): PassportSection[] {
    const result: PassportSection[] = []

    function traverse(root: PassportSection, sub: PassportSection) {
      for (const item of sub.items) {
        if (!item.mappingTo) continue

        const canonicalSaifty = buildSaiftyCanonical(root, sub, item)

        insertDataSpaceItem(item.mappingTo, canonicalSaifty)
      }
    }

    function insertDataSpaceItem(dataSpacePath: string, saiftyCanonical: string) {
      const parts = dataSpacePath.split('.')
      const itemName = parts.pop() || ''
      let current = result

      for (const sectionName of parts) {
        const section = getOrCreateSection(current, sectionName)
        current = section.sections
      }

      const target = getOrCreateSection(result, parts[0])

      target.items.push({
        name: itemName,
        mappingTo: saiftyCanonical,
      })
    }

    function walk(section: PassportSection) {
      for (const child of section.sections) {
        traverse(section, child)
      }
    }

    for (const root of source) {
      walk(root)
    }

    return result
  }

  function buildSaiftyCanonical(rootSection: PassportSection, subSection: PassportSection, item: PassportItem): string {
    const root = rootSection.name // SEC_1
    const level2 = subSection.name // 1

    const itemName = item.name.includes('.') ? item.name.split('.').pop()! : item.name

    if (item.saiftyPrefix) {
      return `${root}_${level2}_${item.saiftyPrefix}.${itemName}`
    }

    return `${root}_${level2}.${itemName}`
  }

  function clearAllMappingsOfTheStructure(sections: PassportSection[]): PassportSection[] {
    function walk(section: PassportSection): PassportSection {
      return {
        ...section,
        items: section.items.map((item) => {
          const { mappingTo, ...rest } = item
          return { ...rest }
        }),
        sections: section.sections.map(walk),
      }
    }

    return sections.map(walk)
  }

  function mappingDataToPassport(
    mappingData: MappingDataItem[],
    from: MappingSystemDefinition,
    to: MappingSystemDefinition
  ): PassportSection[] {
    const flatItems: PassportItem[] = mappingData.map((row) => ({
      name: from.getName(row),
      mappingTo: to.getName(row),
    }))

    const sections = flatItemsToPassportSections(flatItems)
    markArraySections(sections)

    return sections
  }

  function flatItemsToPassportSections(passportItems: PassportItem[]): PassportSection[] {
    const result: PassportSection[] = []

    for (const item of passportItems) {
      const parts = item.name.split('.')
      const itemName = parts.pop() || ''
      let currentSections = result
      let currentSection: PassportSection | undefined

      for (const sectionName of parts) {
        currentSection = getOrCreateSection(currentSections, sectionName)
        currentSections = currentSection.sections
      }

      if (!currentSection) {
        currentSection = getOrCreateSection(result, parts[0])
      }

      currentSection.items.push({
        name: itemName,
        mappingTo: item.mappingTo,
      })
    }

    return result
  }

  function markArraySections(sections: PassportSection[]) {
    function traverse(section: PassportSection) {
      if (isArrayByName(section.name) && !Array.isArray(section.instances)) {
        section.instances = []
      }

      section.sections.forEach(traverse)
    }

    sections.forEach(traverse)
  }

  function isArrayByName(name?: string) {
    return !!name?.includes(MAPPING_ARRAY_SUFFIX)
  }

  function formatDataSpaceItemPath(i: MappingPair) {
    return {
      fieldName: i.saiftyCanonical,
      inputPath: `$.${i.dataSpacePath}`,
      fieldValueType: FieldValueType.Value,
    }
  }

  return {
    MappingSystems,
    JSONToPassport, // JSON = DATA SPACE
    passportToJSON,
    passportToSaifty,
    saiftyToPassport, // Passport = structure
    saiftyToDataSpace,
    dataSpaceToSaifty,
    mappingDataToPassport,
    //
    extractMappingsFromDataSpace,
    clearAllMappingsOfTheStructure,
    formatDataSpaceItemPath,
  }
}
